import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import packageJson from '../package.json';
import { IS_PRODUCTION } from './common/constants';

export const SUPPORTED_LOCALES: Record<string, string> = {
  'en-US': 'English (US)',
  'fr-CA': 'French (CA)',
  'es-US': 'Spanish (US)',
};

// Tracks the app version that last loaded translations, so we can detect an app update.
const I18N_VERSION_STORAGE_KEY = 'SEHomeI18nVersion';

// We use i18n for app localization whereby all user-facing text is available into multiple languages.
// Any time copy is updated - it will need to be updated in the public/locale files.
// See https://translate.i18next.com/ for generating translations
// See https://learn.microsoft.com/en-us/globalization/reference/microsoft-style-guides for guidelines
i18n
  .use(HttpApi)
  .use(LanguageDetector)
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    supportedLngs: Object.keys(SUPPORTED_LOCALES),
    fallbackLng: {
      es: ['es-US'],
      fr: ['fr-CA'],
      en: ['en-US'],
      default: ['en-US'],
    },
    interpolation: {
      escapeValue: false, // react already safes from xss
    },
    load: 'currentOnly',
    debug: !IS_PRODUCTION,
    backend: {
      // Native webviews cache the static translation JSON aggressively, so a content-only
      // copy update never reaches devices. Bust the cache with the app version (changes each
      // release) and tell fetch not to serve a stale cached response.
      queryStringParams: { v: packageJson.version },
      requestOptions: { cache: 'no-cache' },
    },
  })
  .then(reloadResourcesOnVersionChange);

// When the user updates the app, force a one-time refetch of the translation files the first
// time they reopen it, so content-only copy updates land even if the webview cached an older
// response. We only reload when the stored version differs from a *previously seen* one — the
// first-ever launch already loads fresh resources via init(), so there is nothing to bust then.
function reloadResourcesOnVersionChange() {
  const seenVersion = localStorage.getItem(I18N_VERSION_STORAGE_KEY);
  if (seenVersion === packageJson.version) return;

  localStorage.setItem(I18N_VERSION_STORAGE_KEY, packageJson.version);
  if (seenVersion !== null) i18n.reloadResources();
}

export { i18n };
